import { css } from '@emotion/react';
import {
  type FC, useCallback,
  useMemo,
} from 'react';
import { AlternatingStripesComponent } from '~/_shared/baseComponents/alternatingStripes/alternatingStripes';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { MetricProps } from '~/_shared/utils/metric/metrics.types';
import { useGetMetricRegion } from '~/_shared/utils/metric/useGetMetricRegion';
import { useTheme } from '../../themes/theme.hooks';
import { type ThemeProps } from '../../types/themeProps';
import { type MetricsResults } from '../../utils/metric/useMetricData.helpers';
import { MetricsListItemComponent } from './item/metricsListItem.component';
import { groupMetricsDataByColumn } from './metricsList.helpers';

const rootStyle = ({ theme }: ThemeProps) => css({
  width: '100%',
  color: theme.textColors.primary,
});

const metricsWrapperStyle = (theme: Theme) => css({
  borderBottom: `1px solid ${theme.borderColors.primary}`,
  '&:last-of-type': {
    border: 'none',
  },
});

type MetricsListProps = Readonly<{
  metrics: MetricsResults;
  boundaryIsOptimizedTerritoriesTrial?: boolean;
}>;

export const MetricsListComponent: FC<MetricsListProps> = props => {
  const [t] = useTranslation();
  const theme = useTheme();

  const [firstTwoMetricsDataGroupedByColumn, restOfMetricsDataGroupedByColumn] = useMemo(() => {
    const groupedMetrics = groupMetricsDataByColumn(props.metrics.spreadsheetColumnMetrics, t);
    return [groupedMetrics.slice(0, 2), groupedMetrics.slice(2)];
  }, [props.metrics.spreadsheetColumnMetrics, t]);

  const { getMetricRegion } = useGetMetricRegion();

  const renderMetricName = useCallback((metric: MetricProps) => {
    const metricRegion = metric.demographicId ? getMetricRegion(metric.demographicId) : undefined;
    if (metricRegion) {
      return `${metric.name} (${metricRegion})`;
    }

    return metric.name;
  }, [getMetricRegion]);

  return (
    <div
      css={rootStyle({ theme })}
    >
      <AlternatingStripesComponent css={metricsWrapperStyle}>
        {props.metrics.demographicMetrics.length > 0 && (
          props.metrics.demographicMetrics.map((metric, index) => (
            <MetricsListItemComponent
              key={index}
              name={renderMetricName(metric)}
              single={{
                ...metric,
              }}
              showApproximateSymbol
              blurMetrics={props.boundaryIsOptimizedTerritoriesTrial}
            />
          ))
        )}

        {firstTwoMetricsDataGroupedByColumn.map((metric) => (
          <MetricsListItemComponent
            key={metric.name + metric.subheader}
            {...metric}
          />
        ))}
        {restOfMetricsDataGroupedByColumn.map((metric) => (
          <MetricsListItemComponent
            key={metric.name + metric.subheader}
            blurMetrics={props.boundaryIsOptimizedTerritoriesTrial}
            {...metric}
          />
        ))}
      </AlternatingStripesComponent>
    </div>
  );
};
